import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const ThemeContext = createContext(null);

const THEME_KEY = 'acountix-theme';           // 'light' | 'dark'
const MODE_KEY = 'acountix-theme-mode';       // 'auto' (óra szerint) | 'manual'
const TILE_STYLE_KEY = 'acountix-tile-style'; // 'clean' | 'vivid'

export const TILE_STYLES = ['clean', 'vivid'];
export const DARK_FROM = 18; // 18:00-tól sötét
export const DARK_TO = 6;    // 06:00-tól világos

const isWorkerPath = () => typeof window !== 'undefined' && /^\/(me|dolgozo|w\/|hj\/)/.test(window.location.pathname);
export const themeByHour = (d = new Date()) => { const h = d.getHours(); return (h >= DARK_FROM || h < DARK_TO) ? 'dark' : 'light'; };

function getInitialMode() {
  try {
    const m = localStorage.getItem(MODE_KEY);
    if (m === 'auto' || m === 'manual') return m;
  } catch { /* ignore */ }
  return isWorkerPath() ? 'auto' : 'manual';
}

function getInitialTheme() {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch { /* localStorage may be unavailable */ }
  if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

function getInitialTileStyle() {
  try {
    const stored = localStorage.getItem(TILE_STYLE_KEY);
    if (TILE_STYLES.includes(stored)) return stored;
  } catch { /* ignore */ }
  return 'clean';
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(getInitialMode);
  const [theme, setThemeState] = useState(() => (getInitialMode() === 'auto' ? themeByHour() : getInitialTheme()));
  const [tileStyle, setTileStyleState] = useState(getInitialTileStyle);

  // auto mód: percenként újraértékel (18:00 → sötét, 06:00 → világos)
  useEffect(() => {
    if (mode !== 'auto') return undefined;
    const tick = () => setThemeState(themeByHour());
    tick();
    const id = setInterval(tick, 60 * 1000);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [mode]);
  useEffect(() => { try { localStorage.setItem(MODE_KEY, mode); } catch { /* ignore */ } }, [mode]);
  const setTheme = useCallback((t) => { setModeState('manual'); setThemeState(t); }, []);
  const setMode = useCallback((m) => { if (m === 'auto' || m === 'manual') setModeState(m); }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    root.setAttribute('data-theme', theme);
    root.removeAttribute('data-palette');
    try { localStorage.setItem(THEME_KEY, theme); localStorage.removeItem('acountix-palette'); } catch { /* ignore */ }
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-tile-style', tileStyle);
    try { localStorage.setItem(TILE_STYLE_KEY, tileStyle); } catch { /* ignore */ }
  }, [tileStyle]);

  const toggleTheme = useCallback(() => {
    setModeState('manual');
    setThemeState(prev => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const setTileStyle = useCallback((s) => {
    if (TILE_STYLES.includes(s)) setTileStyleState(s);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, mode, setMode, tileStyle, setTileStyle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
